import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface CompletedJob {
  id: string;
  owner_name: string;
  property_address: string;
  property_city: string;
  property_state: string;
  latitude: number;
  longitude: number;
  roof_type: string;
  estimated_value: number;
  updated_at: string;
  distance_miles?: number;
  photo_url?: string;
}

// Haversine formula to calculate distance between two points
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

export function useNearbyCompletedJobs(
  centerLat: number | null,
  centerLng: number | null,
  radiusMiles: number = 5
) {
  const { orgId } = useAuth();
  const [jobs, setJobs] = useState<CompletedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || centerLat === null || centerLng === null) {
      setLoading(false);
      return;
    }

    const fetchNearbyJobs = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all completed jobs with coordinates for the org
        const { data: jobsData, error: jobsError } = await supabase
          .from("jobs")
          .select(`
            id,
            owner_name,
            property_address,
            property_city,
            property_state,
            latitude,
            longitude,
            roof_type,
            estimated_value,
            updated_at
          `)
          .eq("org_id", orgId)
          .ilike("status", "%complete%")
          .not("latitude", "is", null)
          .not("longitude", "is", null);

        if (jobsError) throw jobsError;

        // Calculate distances and filter by radius
        const nearbyJobs: CompletedJob[] = [];

        for (const job of jobsData || []) {
          if (job.latitude && job.longitude) {
            const distance = calculateDistance(
              centerLat,
              centerLng,
              job.latitude,
              job.longitude
            );

            if (distance <= radiusMiles) {
              nearbyJobs.push({
                ...job,
                distance_miles: Math.round(distance * 10) / 10,
              });
            }
          }
        }

        // Sort by distance
        nearbyJobs.sort((a, b) => (a.distance_miles || 0) - (b.distance_miles || 0));

        // Fetch first photo for each job
        const jobIds = nearbyJobs.map((j) => j.id);
        if (jobIds.length > 0) {
          const { data: photosData } = await supabase
            .from("photos")
            .select("job_id, url")
            .in("job_id", jobIds)
            .order("created_at", { ascending: false });

          // Create a map of job_id to first photo
          const photoMap: Record<string, string> = {};
          for (const photo of photosData || []) {
            if (!photoMap[photo.job_id]) {
              photoMap[photo.job_id] = photo.url;
            }
          }

          // Add photo URLs to jobs
          for (const job of nearbyJobs) {
            job.photo_url = photoMap[job.id];
          }
        }

        setJobs(nearbyJobs);
      } catch (err: any) {
        console.error("Error fetching nearby jobs:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNearbyJobs();
  }, [orgId, centerLat, centerLng, radiusMiles]);

  return { jobs, loading, error, count: jobs.length };
}
